import React, { useEffect, useState } from 'react'
import { CaretLeft, Plus, Trash } from '@phosphor-icons/react'
import { Button, TimePicker } from 'antd'
import SelectDatesCalendar from 'components/global/SelectDatesCalendar'
import './styles/styles.scss'
import moment, { Moment } from 'moment'
import { SpecialSchedule } from 'types/globalTypes'
import {
  combineDateAndTimeToUtcString,
  convertMinutesToLocalTime,
  convertTimeToUtcMinutes,
} from 'helper/utility'
import SpecialScheduleModal from '../../../CreateEvent/Screens/GeneralDetails/GeneralDetailModal/AddSpecialScheduleModal'
import { useLeagueStep3 } from 'api/league/LeagueCreation/leagueCreation'
import { leagueEventStore } from 'state/league/leagueEvent'
import ButtonSpinner from 'components/global/ButtonSpinner'

interface Step3Props {
  handleNext: () => void
  handleBack: () => void
}

const Step3: React.FC<Step3Props> = ({ handleNext, handleBack }) => {
  const { leagueData, setLeagueData } = leagueEventStore()
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  // Month currently shown in the calendar (kept in sync via onActiveMonthChange).
  const [activeMonth, setActiveMonth] = useState<Date>(new Date())
  const [defaultTime, setDefaultTime] = useState({
    startTime: moment('9:00', 'HH:mm'),
    endTime: moment('15:00', 'HH:mm'),
  })
  const [specialScheduleData, setSpecialScheduleData] = useState([])
  const [addSpecialScheduleModalVisible, setAddSpecialScheduleModalVisible] =
    useState<boolean>(false)

  const handleStartTimeChange = (time: Moment | null, timeString: string) => {
    setDefaultTime({ ...defaultTime, startTime: moment(time) })
  }
  const handleEndTimeChange = (time: Moment | null, timeString: string) => {
    setDefaultTime({ ...defaultTime, endTime: moment(time) })
  }

  const removeSpecialscheduleInput = (index: number) => {
    const current: any = specialScheduleData[index]
    setSpecialScheduleData([
      ...specialScheduleData.filter((data: any) => data.day !== current.day),
    ])
  }

  const { mutate: leagueStep3Mutation, isLoading: leagueStep3Loading } =
    useLeagueStep3()

  const handleSubmit = () => {
    const payload = {
      datesOfEvent: selectedDates.map((item: string) =>
        combineDateAndTimeToUtcString(
          item,
          moment(defaultTime.startTime).format('hh:mm A'),
        ),
      ),
      timings: {
        startTime: convertTimeToUtcMinutes(defaultTime.startTime as any),
        endTime: convertTimeToUtcMinutes(defaultTime.endTime as any),
        specialSchedules: specialScheduleData.map((data: SpecialSchedule) => ({
          ...data,
          day: moment(data.day, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]'),
        })),
      },
    }
    leagueStep3Mutation(
      { leagueId: leagueData._id, payload },
      {
        onSuccess: data => {
          setLeagueData({ ...leagueData, ...data })
          handleNext()
        },
      },
    )
  }

  useEffect(() => {
    if (leagueData?.timings) {
      setSelectedDates(
        leagueData.datesOfEvent.map(d => moment(d).format('YYYY-MM-DD')),
      )
      setDefaultTime({
        startTime: moment(
          convertMinutesToLocalTime(leagueData?.timings.startTime, 'HH:mm'),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime(leagueData?.timings.endTime, 'HH:mm'),
          'HH:mm',
        ),
      })
      setSpecialScheduleData(leagueData.timings?.specialSchedules)
    }
  }, [leagueData?.timings])

  // Selectable days of the currently-shown month (today onward — mirrors the
  // calendar's own rule for the non-limited League case), tagged weekday/weekend.
  const getMonthDays = () => {
    const year = activeMonth.getFullYear()
    const monthIndex = activeMonth.getMonth()
    const first = new Date(year, monthIndex, 1)
    const last = new Date(year, monthIndex + 1, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const days: { date: string; isWeekday: boolean }[] = []
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      const copy = new Date(d)
      if (copy.getTime() >= today.getTime()) {
        const day = copy.getDay()
        days.push({
          date: moment(copy).format('YYYY-MM-DD'),
          isWeekday: day !== 0 && day !== 6,
        })
      }
    }
    return days
  }

  const handleSelectAllDays = () => {
    const monthDates = getMonthDays().map(x => x.date)
    if (monthDates.length === 0) return
    const allSelected = monthDates.every(d => selectedDates.includes(d))
    if (allSelected) {
      setSelectedDates(selectedDates.filter(sd => !monthDates.includes(sd)))
    } else {
      setSelectedDates(Array.from(new Set([...selectedDates, ...monthDates])))
    }
  }

  const handleSelectWeekdays = () => {
    const all = getMonthDays()
    const weekdayDates = all.filter(x => x.isWeekday).map(x => x.date)
    const weekendDates = all.filter(x => !x.isWeekday).map(x => x.date)
    if (weekdayDates.length === 0) return

    const allWeekdaysSelected = weekdayDates.every(d =>
      selectedDates.includes(d),
    )
    const anyWeekendSelected = weekendDates.some(d => selectedDates.includes(d))

    if (allWeekdaysSelected && !anyWeekendSelected) {
      // Already weekdays-only → toggle them off.
      setSelectedDates(selectedDates.filter(sd => !weekdayDates.includes(sd)))
    } else {
      // Narrow to weekdays: drop this month's weekends, add all weekdays.
      // (Handles coming from "All days of month", a partial selection, or empty.)
      const filtered = selectedDates.filter(sd => !weekendDates.includes(sd))
      setSelectedDates(Array.from(new Set([...filtered, ...weekdayDates])))
    }
  }

  return (
    <>
      <div className="calendar-width">
        <SelectDatesCalendar
          isLimited={false}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          showSelectDatesButtons={false}
          onActiveMonthChange={setActiveMonth}
        />
        <div className="mt-15px w-full flex justify-center gap-10px">
          <button className="btn-secondary" onClick={handleSelectAllDays}>
            All days of month
          </button>
          <button className="btn-secondary" onClick={handleSelectWeekdays}>
            All weekdays of month
          </button>
        </div>
      </div>

      <div className="schedule-width">
        <div className="default-schedule">
          <h2 className="text-14 w-600">Default schedule</h2>
          <div className="flex align-center gap-16px w-50p">
            <TimePicker
              className="w-100 form-control-field"
              placeholder="Start Time"
              format="h:mm a"
              value={defaultTime.startTime}
              showNow={false}
              clearIcon={false}
              onChange={handleStartTimeChange}
            />
            <div>-</div>
            <TimePicker
              className="w-100 form-control-field"
              placeholder="End Time"
              format="h:mm a"
              value={defaultTime.endTime}
              showNow={false}
              clearIcon={false}
              onChange={handleEndTimeChange}
            />
          </div>
        </div>

        <div className="special-schedule">
          <h2 className="text-14 w-600">Special schedule</h2>
          <Plus
            className="pointer"
            size={20}
            color="#000"
            onClick={() => setAddSpecialScheduleModalVisible(true)}
          />
        </div>
        {specialScheduleData.length > 0 && (
          <div>
            {specialScheduleData.map(
              (specialSchedule: SpecialSchedule, index: number) => {
                return (
                  <div className="special-schedule-list" key={index}>
                    <h4 className="text-14 w-600">
                      {moment(specialSchedule.day).format('DD-MM-YYYY')}
                    </h4>
                    <div className="time-input">
                      <div className="flex justify-between align-center w-full gap-20px">
                        <div className="flex align-center gap-16px">
                          <TimePicker
                            className="w-100 form-control-field"
                            placeholder="Start Time"
                            format="h:mm a"
                            showNow={false}
                            clearIcon={false}
                            value={moment(
                              convertMinutesToLocalTime(
                                specialSchedule.startTime,
                              ),
                              'hh:mm A',
                            )}
                            onChange={handleStartTimeChange}
                            disabled
                          />
                          <div>-</div>
                          <TimePicker
                            className="w-100 form-control-field"
                            placeholder="End Time"
                            format="h:mm a"
                            showNow={false}
                            clearIcon={false}
                            value={moment(
                              convertMinutesToLocalTime(
                                specialSchedule.endTime,
                              ),
                              'hh:mm A',
                            )}
                            onChange={handleEndTimeChange}
                            disabled
                          />
                        </div>

                        <Trash
                          size={20}
                          color={'#FF6961'}
                          className="pointer"
                          onClick={() => removeSpecialscheduleInput(index)}
                        />
                      </div>
                    </div>
                  </div>
                )
              },
            )}
          </div>
        )}
      </div>

      <div className="mt-15px event-button-cont">
        <h5
          className="flex gap-10px align-center pointer"
          onClick={() => handleBack()}
        >
          <CaretLeft size={20} color="#000" />
          <span className="text-15 w-500">Back</span>
        </h5>
        <Button
          className="stepper-button"
          disabled={leagueStep3Loading}
          onClick={handleSubmit}
        >
          {leagueStep3Loading ? (
            <ButtonSpinner size={20} color="#000" />
          ) : (
            'Next'
          )}
        </Button>
      </div>

      {addSpecialScheduleModalVisible && (
        <SpecialScheduleModal
          addSpecialScheduleModalVisible={addSpecialScheduleModalVisible}
          setAddSpecialScheduleModalVisible={setAddSpecialScheduleModalVisible}
          selectedDates={selectedDates}
          setSpecialScheduleData={setSpecialScheduleData}
          specialScheduleData={specialScheduleData}
          // eventTime={eventTime}
          // setEventTime={setEventTime}
        />
      )}
    </>
  )
}

export default Step3
